import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { canCreateMission, canSeeMissions, formatPeriod, missionErrorMessage } from "../../lib/missions";
import { hasPermission } from "../../lib/permissions";
import { MissionStatusBadge } from "./MissionBits";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { ChevronRight, MapPinned, Plus } from "lucide-react";

const LIMIT = 20;

function missionCreateLink(prospectId, pilotId) {
  const params = new URLSearchParams({ prospect: prospectId });
  if (pilotId) params.set("pilot", pilotId);
  return `/missions/nouvelle?${params.toString()}`;
}

// The missions of one pharmacy, on the prospect page and in the pilot workspace. The server only returns the missions the
// person may see; creating one from here also needs to see the pharmacy (prospects:view), like the form itself.
export default function RelatedMissions({ prospectId, pilotId = null, testid = "related-missions" }) {
  const { user } = useAuth();
  const visible = canSeeMissions(user);
  const canCreate = canCreateMission(user) && hasPermission(user, "prospects:view");
  const [state, setState] = useState({ phase: "loading", items: [], total: 0, error: "" });

  const load = useCallback(async () => {
    if (!visible || !prospectId) return;
    try {
      const { data } = await api.get("/missions", { params: { prospect_id: prospectId, limit: LIMIT } });
      setState({ phase: "ready", items: data.items, total: data.total, error: "" });
    } catch (e) {
      setState({ phase: "error", items: [], total: 0, error: missionErrorMessage(e, "Impossible de charger les missions.") });
    }
  }, [visible, prospectId]);
  useEffect(() => { load(); }, [load]);

  if (!visible) return null;
  const { phase, items, total } = state;

  return (
    <Card className="p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><MapPinned size={16} className="text-slate-400" />Missions</h3>
        {canCreate && (
          <Button asChild variant="outline" size="sm">
            <Link to={missionCreateLink(prospectId, pilotId)} data-testid={`${testid}-new`}><Plus size={14} className="mr-1.5" />Nouvelle mission</Link>
          </Button>
        )}
      </div>
      {phase === "loading" && <Skeleton className="h-14 w-full" />}
      {phase === "error" && (
        <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">{state.error}<Button variant="outline" size="sm" onClick={load}>Réessayer</Button></div>
      )}
      {phase === "ready" && items.length === 0 && (
        <p className="text-sm text-slate-500" data-testid={`${testid}-empty`}>Aucune mission pour cette pharmacie{canCreate ? " : planifiez une installation, une formation ou une visite." : "."}</p>
      )}
      {phase === "ready" && items.length > 0 && (
        <div className="divide-y divide-slate-100 -mx-1">
          {items.map((m) => (
            <Link key={m.id} to={`/missions/${m.id}`} className="flex items-center gap-3 px-1 py-2.5 rounded hover:bg-slate-50" data-testid={`${testid}-item-${m.reference}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 break-words">{m.title}</span>
                  <MissionStatusBadge status={m.status} />
                </div>
                <div className="text-xs text-slate-500">{m.reference} · {formatPeriod(m.planned_start_at, m.planned_end_at, m.timezone)} · {m.lead ? m.lead.name : "Responsable à désigner"}</div>
              </div>
              <ChevronRight size={15} className="text-slate-300 shrink-0" />
            </Link>
          ))}
          {total > items.length && (
            <p className="px-1 pt-2 text-xs text-slate-500">{total - items.length} mission{total - items.length > 1 ? "s" : ""} plus ancienne{total - items.length > 1 ? "s" : ""} : voir la page Missions.</p>
          )}
        </div>
      )}
    </Card>
  );
}
